import { NAME_ALLOWED_CHARACTERS } from '@app/constants/regExp';
import { getChangeBuyersData, resetEditBuyerData, updateBuyersDataRequest } from '@app/ducks';
import { paths } from '@app/paths';
import { useValidatedForm } from '@app/utils/hooks/useValidatedForm';
import { getPhoneSchema } from '@app/utils/schemas';
import { Buyer, BuyerDataChangePayloadDTO } from '@typings';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import * as yup from 'yup';

import { Input } from '../various/Fields/Input';
import { FieldWrapper, Form, FormFieldset, FormGrid, InputField } from '../various/Form';

import { AccountLayout } from './AccountLayout';

type FormData = BuyerDataChangePayloadDTO;

interface Props {
  buyer: Buyer | Empty.Object;
}

export const BuyerForm = ({ buyer }: Props) => {
  const { t } = useTranslation(['accounts', 'common', 'validation']);
  const dispatch = useDispatch();
  const {
    isLoading,
    data: { isChanged },
  } = useSelector(getChangeBuyersData);

  React.useEffect(() => {
    if (isChanged) {
      dispatch(resetEditBuyerData());
    }
  }, [dispatch, isChanged]);

  const defaultValues: FormData = {
    firstName: buyer.firstName,
    lastName: buyer.lastName,
    mobileNumber: buyer.mobileNumber,
    phoneNumber: buyer.phoneNumber,
  };

  const validationSchema: yup.ObjectSchema<FormData> = yup.object({
    firstName: yup.string().required(t('validation:first_name_hint')).matches(NAME_ALLOWED_CHARACTERS, t('validation:valid_name_hint')),
    lastName: yup.string().required(t('validation:last_name_hint')).matches(NAME_ALLOWED_CHARACTERS, t('validation:valid_name_hint')),
    mobileNumber: getPhoneSchema(),
    phoneNumber: getPhoneSchema(),
  });

  const formMethods = useValidatedForm<FormData>({
    defaultValues,
    validationSchema,
  });

  const { register, reset, formState } = formMethods;

  const handleSubmit = (values: FormData) => {
    dispatch(updateBuyersDataRequest(values));
  };

  const formName = 'buyerForm';

  return (
    <AccountLayout activePath={paths.ACCOUNT_INFO} formName={formName} hasChanges={formState.isDirty} onCancel={() => reset()}>
      <section className="box flex flex-col gap-5">
        <h3 className="heading-3">{t('accounts:account_information')}</h3>
        <p className="text-neutral-40 body-2">{t('accounts:account_information_subtitle')}</p>
        <Form id={formName} formMethods={formMethods} onSubmit={handleSubmit}>
          <FormFieldset isDisabled={isLoading}>
            <FormGrid rowGap="medium">
              <InputField label={t('common:first_name')} isRequired {...register('firstName')} />
              <InputField label={t('common:last_name')} isRequired {...register('lastName')} />
              <FieldWrapper label={t('common:email')} helpMessage={t('accounts:email_disabled_tooltip')}>
                <Input value={buyer.email} isReadonly />
              </FieldWrapper>
              <InputField label={t('common:phone_number')} type="tel" isRequired={false} {...register('phoneNumber')} />
              <InputField label={t('common:mobile_number')} type="tel" isRequired={false} {...register('mobileNumber')} />
            </FormGrid>
          </FormFieldset>
        </Form>
      </section>
    </AccountLayout>
  );
};
